import React, { useEffect } from 'react'
import { ModernNavbar } from './ModernNavbar'
import { useLiveSync } from '@/hooks/useLiveSync'
import { useFullscreen } from '@/hooks/useFullscreen'
import { usePOSStore } from '@/store/posStore'
import { useAuth } from '@/contexts/AuthContext'

interface LayoutProps {
  children: React.ReactNode
}

export const DashboardLayout: React.FC<LayoutProps> = ({ children }) => {
  const { toggle: toggleFullscreen } = useFullscreen()
  const { user } = useAuth()
  const { initRealtimeListeners, cleanupListeners } = usePOSStore()

  // Initialize cross-tab live sync (BroadcastChannel + visibility-based refresh)
  useLiveSync()

  // Start Firestore real-time listeners once the user is authenticated.
  // Cleans up on logout or unmount to release WebSocket connections.
  useEffect(() => {
    if (!user) return
    initRealtimeListeners()
    return () => {
      cleanupListeners()
    }
  }, [user?.id ?? user])

  // F11 toggles fullscreen (especially on POS page)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault()
        toggleFullscreen()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggleFullscreen])

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background flex flex-col">
      {/* Modern Navigation */}
      <ModernNavbar />
      
      {/* Main Content - full width to use available space */}
      <main className="relative w-full flex-1">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 py-8">
          {children}
        </div>
      </main>
      <footer className="w-full border-t border-border/50 bg-gradient-to-r from-background/60 via-background/80 to-background/60 backdrop-blur-xl">
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 py-4 text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-center gap-2">
          <span>© {new Date().getFullYear()} Sachin Bookshop. All rights reserved.</span>
          <span className="hidden sm:inline">•</span>
          <span>Powered by Seyronyx Labs</span>
        </div>
      </footer>
    </div>
  )
}